import cloudflareAssets from "@/assets/cloudflareAssets";
import type { ICardOnlineTransaction } from "@/modules/card/common/types";
import { useGetCardOnlineTransactionsQuery } from "@/modules/card/services";
import { INITIAL_PAGE, PageSizeOptions } from "@/shared/common/constants";
import { CloudflareAsset, ErrorMessages, TableNames } from "@/shared/common/enums";
import type { IError } from "@/shared/common/types";
import { CloudFlareImage, FilterPanel, Select } from "@/shared/components";
import { getErrorMessage, getTableName } from "@/shared/helpers";
import { useFilters, useTableHooks } from "@/shared/hooks";
import {
  AdvancedTable as Table,
  ColumnSettings
} from "@ab.uitools/ui-library/components/AdvancedTable";
import { Button } from "@ab.uitools/ui-library/components/Button";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { getColumns } from "./columns";

export const CardOnlineTransactionsHistory = () => {
  const { cardId = "" } = useParams();
  const { t } = useTranslation();
  const { filters, setFilters, defaultFilters } = useFilters();

  const [cardHistory, setCardHistory] = useState<ICardOnlineTransaction[]>([]);

  const {
    page,
    pageSize,
    tableRef,
    hasNextPage,
    onNextPage,
    onPrevPage,
    setHasNextPage,
    onPageSizeChange,
    onPageIndexReset,
    updateHasNextPage,
  } = useTableHooks<ICardOnlineTransaction>();

  const {
    data: list,
    isFetching,
    isError,
    error,
  } = useGetCardOnlineTransactionsQuery(
    {
      cardId,
      pageIndex: page,
      pageSize,
      ...filters,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const columns = getColumns({ t });

  useEffect(() => {
    if (!list) return;

    if (list?.length || (!list?.length && page === INITIAL_PAGE)) {
      setCardHistory(list);
      updateHasNextPage(list.length);
    } else if (!list?.length && page !== INITIAL_PAGE) {
      onPrevPage();
      setHasNextPage(false);
    }
  }, [list]);

  const tableName = getTableName(TableNames.CardOnlineHistory);

  return (
    <Table
      data={cardHistory || []}
      withSelect
      columns={columns}
      isLoading={isFetching || !tableName}
      hasError={isError}
      tableSettings={{
        tableName: tableName || "",
        persistColumnSettings: "localStorage"
      }}
      emptyTitle={
        isError ?
          t(getErrorMessage(error as IError, ErrorMessages.TryAgainShortly)) :
          t("general.noData")
      }
      emptySubTitle={
        !isError ? t("general.noDataAvailable") : ""
      }
      emptyIllustration={cloudflareAssets[CloudflareAsset.EmptyContent]}
      renderHeader={(table) => (
        <div className="advanced-table__header flexbox justify-content--end py-16 px-24">
          <FilterPanel
            filters={filters}
            setFilters={setFilters}
            defaultFilters={defaultFilters}
            resetPagination={onPageIndexReset}
          />
          <div className="flexbox gap-12">
            <ColumnSettings
              table={table}
              allToggleText={t("general.all")}
              tooltipText={t("general.columnCantBeHidden")}
            />
          </div>
        </div>
      )}
      renderFooter={(table) => {
        tableRef.current = table;
        return (
          <div className="advanced-table__footer">
            <div className="advanced-table__pagination">
              <Select
                setSelectedValue={(value) => onPageSizeChange(value as string)}
                selectedValue={`${pageSize}`}
                options={PageSizeOptions}
              />
              <div className="flexbox align-items--center">
                <Button
                  onClick={onPrevPage}
                  size="medium"
                  type="tertiary"
                  data-testid="card-online-history-prev-page"
                  data-ga-id="card-online-history-prev-page"
                  data-ga-action="Card online history table previos page button"
                  iconProps={{
                    Component: (props: ISVGIconProps) => (
                      <CloudFlareImage {...props} path={CloudflareAsset.ArrowShortLeft}/>
                    ),
                  }}
                  disabled={page === INITIAL_PAGE}
                />
                <Button
                  onClick={onNextPage}
                  size="medium"
                  type="tertiary"
                  data-testid="card-online-history-next-page"
                  data-ga-id="card-online-history-next-page"
                  data-ga-action="Card online history table next page button"
                  iconProps={{
                    Component: (props: ISVGIconProps) => (
                      <CloudFlareImage {...props} path={CloudflareAsset.ArrowShortRight}/>
                    ),
                  }}
                  disabled={!hasNextPage}
                />
              </div>
            </div>
          </div>
        );
      }}
    />
  );
};
